import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
} from "@mui/material";
import { formatR } from "../../../../../../utils/money";

interface PackageProps {
  lineItems?: any[];
}

// Mirrors the backend: "VAT15" -> 0.15; non-VAT / blank -> 0.
const vatRate = (taxCode?: string): number => {
  if (!taxCode) return 0;
  const digits = String(taxCode).replace(/[^0-9]/g, "");
  return digits ? parseInt(digits, 10) / 100 : 0;
};

// Normalizes a saved quotation line item (backend casing may vary).
const normalize = (li: any) => {
  const name =
    li?.name ??
    li?.description ??
    li?.quotableItemName ??
    li?.quotable_item_name ??
    li?.serviceName ??
    "Service";
  const quantity = Number(li?.quantity ?? li?.qty ?? 1) || 1;
  const unitPrice = Number(li?.unitPrice ?? li?.unit_price ?? 0) || 0;
  const taxCode = li?.taxCode ?? li?.tax_code ?? "";
  const subtotal = unitPrice * quantity;
  const vat = subtotal * vatRate(taxCode);
  return { name, quantity, unitPrice, subtotal, vat, total: subtotal + vat };
};

export default function Package({ lineItems = [] }: PackageProps) {
  const rows = (lineItems ?? []).map(normalize);
  const subtotal = rows.reduce((s, r) => s + r.subtotal, 0);
  const vatTotal = rows.reduce((s, r) => s + r.vat, 0);
  const grandTotal = subtotal + vatTotal;

  return (
    <Box mt={4}>
      <Typography
        fontWeight={600}
        fontSize={22}
        mb={2}
        textAlign="left"
        textTransform="uppercase"
      >
        Quotation Services
      </Typography>

      {rows.length === 0 ? (
        <Box
          sx={{
            border: "1px dashed #E5E7EB",
            borderRadius: 2,
            py: 6,
            textAlign: "center",
          }}
        >
          <Typography fontSize={14} color="#6B7280" fontWeight={600}>
            No services on this quotation.
          </Typography>
        </Box>
      ) : (
        <>
          <Paper sx={{ mb: 4 }} elevation={0}>
            <Table>
              <TableHead sx={{ bgcolor: "#f7f7f7" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Service Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Qty</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Subtotal</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>VAT</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, index) => (
                  <TableRow key={`${r.name}-${index}`} sx={{ bgcolor: "#ffffff" }}>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>{r.quantity}</TableCell>
                    <TableCell>{formatR(r.unitPrice)}</TableCell>
                    <TableCell>{formatR(r.subtotal)}</TableCell>
                    <TableCell>{formatR(r.vat)}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{formatR(r.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Box mt={4}>
            <Box display="flex" justifyContent="space-between" py={1}>
              <Typography>Subtotal (excl. VAT)</Typography>
              <Typography fontWeight={600}>{formatR(subtotal)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" py={1}>
              <Typography>VAT</Typography>
              <Typography fontWeight={600}>{formatR(vatTotal)}</Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box display="flex" justifyContent="space-between">
              <Typography fontWeight={700} fontSize={18}>
                Total Quotation Amount
              </Typography>
              <Typography fontWeight={700} fontSize={18}>
                {formatR(grandTotal)}
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
